import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { API_URL } from '@/lib/utils';
import { Phone, Mail, MapPin, Clock, MessageCircle, Loader2, CheckCircle } from 'lucide-react';

const services = [
  'NABH Accreditation',
  'NABL Certification',
  'Healthcare Courses',
  'Digital Healthcare (EMR/HIS)',
  'Hospital Planning',
  'TPA & Empanelment',
  'CME & Workshops',
  'Other'
];

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/leads`, {
        ...formData,
        source: 'contact-page'
      });
      setSubmitted(true);
      toast.success('Message sent successfully! We will contact you soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Us - Get Free Healthcare Consultation"
        description="Contact Steward Healthcare India for NABH consultancy, digital healthcare solutions, and healthcare courses. Office: Panipat, Haryana. Call: +91 9616298162. WhatsApp available."
        keywords="contact steward healthcare, healthcare consulting Panipat, NABH consultant contact, healthcare training enquiry"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Contact Us</span>
          <h1 className="font-manrope text-4xl md:text-5xl font-bold text-[#1a2a5e] mt-2 mb-6">
            Get in Touch with Us
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out to us and our team will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-manrope text-2xl font-bold text-[#1a2a5e] mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Feel free to reach out to us through any of the following channels. We're always ready to assist you.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1a2a5e] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2a5e] mb-1">Office Address</h3>
                    <p className="text-gray-600">
                      SCO 23, 2nd Floor, Hi Street Market,<br />
                      Eldeco Estate One, Panipat-132103<br />
                      Haryana, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2eb24c] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2a5e] mb-1">Phone</h3>
                    <a href="tel:+919616298162" className="text-gray-600 hover:text-[#1a2a5e]">
                      +91 9616298162
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3b7dd8] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2a5e] mb-1">Email</h3>
                    <a href="mailto:info@stewardindia.com" className="text-gray-600 hover:text-[#1a2a5e]">
                      info@stewardindia.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2a5e] mb-1">WhatsApp</h3>
                    <a 
                      href="https://wa.me/919616298162" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#1a2a5e]"
                    >
                      Chat with us
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2a5e] mb-1">Business Hours</h3>
                    <p className="text-gray-600">
                      Mon - Sat: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="flex flex-wrap gap-3 pt-6">
                <Button asChild className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                  <a href="tel:+919616298162">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </a>
                </Button>
                <Button asChild className="bg-[#25D366] hover:bg-[#128C7E]">
                  <a href="https://wa.me/919616298162" target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="shadow-2xl border-0">
                <CardContent className="p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                      <p className="text-gray-600 mb-6">
                        Your message has been received. Our team will contact you within 24 hours.
                      </p>
                      <Button onClick={() => setSubmitted(false)} variant="outline">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-manrope text-xl font-bold text-[#1a2a5e] mb-6">Send us a Message</h3>
                      <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              placeholder="Your full name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="h-12"
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email Address *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              placeholder="your@email.com"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="h-12"
                              data-testid="contact-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder="+91 9876543210"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              className="h-12"
                              data-testid="contact-phone-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service">Service Interested In</Label>
                            <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                              <SelectTrigger className="h-12" data-testid="contact-service-select">
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                              <SelectContent>
                                {services.map((service) => (
                                  <SelectItem key={service} value={service}>{service}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Your Message</Label>
                          <Textarea
                            id="message"
                            name="message"
                            placeholder="Tell us about your requirements..."
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            data-testid="contact-message-input"
                          />
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full h-12 bg-[#1a2a5e] hover:bg-[#2d4a8e] text-base"
                          disabled={loading}
                          data-testid="contact-submit-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            'Send Message'
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-manrope text-2xl font-bold text-[#1a2a5e] mb-8 text-center">Find Us</h2>
          <div className="map-container aspect-video md:aspect-[21/9]">
            <iframe
              src="https://maps.google.com/maps?q=Steward+Healthcare+India+Pvt+LTD+NABH+Consultant+Panipat&ll=29.4475366,76.9704239&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Steward Healthcare Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
